import React, { useEffect, useState, useMemo, useCallback } from "react";
import DataTable from "react-data-table-component";
import Header from "./Header";
import Footer from "./Footer";
import API_BASE_URL from "../Config";
import "../styles/global.css";

/* ────────── SVG Icon System ────────── */
const Icon = ({ d, size = 18, stroke = 1.8 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={stroke}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: "block", flexShrink: 0 }}
  >
    {d}
  </svg>
);

const SearchIcon = (p) => <Icon {...p} d={<><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>} />;
const PlusIcon = (p) => <Icon {...p} d={<><path d="M12 5v14M5 12h14" /></>} />;
const EditIcon = (p) => <Icon {...p} d={<><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4Z" /></>} />;
const TrashIcon = (p) => <Icon {...p} d={<><path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M19 6 18 20a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /></>} />;
const CloseIcon = (p) => <Icon {...p} d={<><path d="M18 6 6 18M6 6l12 12" /></>} />;
const TagIcon = (p) => <Icon {...p} d={<><path d="M12 2H2v10l9.29 9.29a1 1 0 0 0 1.41 0l7.59-7.59a1 1 0 0 0 0-1.41L12 2z" /><path d="M7 7h.01" /></>} />;
const ChevronLeftIcon = (p) => <Icon {...p} d={<><path d="m15 18-6-6 6-6" /></>} />;
const ChevronRightIcon = (p) => <Icon {...p} d={<><path d="m9 18 6-6-6-6" /></>} />;
const CheckIcon = (p) => <Icon {...p} d={<><path d="M20 6 9 17l-5-5" /></>} />;
const XCircleIcon = (p) => <Icon {...p} d={<><circle cx="12" cy="12" r="10" /><path d="m15 9-6 6M9 9l6 6" /></>} />;
const CheckCircleIcon = (p) => <Icon {...p} d={<><circle cx="12" cy="12" r="10" /><path d="m9 12 2 2 4-4" /></>} />;
const PercentIcon = (p) => <Icon {...p} d={<><line x1="19" y1="5" x2="5" y2="19" /><circle cx="6.5" cy="6.5" r="2.5" /><circle cx="17.5" cy="17.5" r="2.5" /></>} />;
const CalendarIcon = (p) => <Icon {...p} d={<><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>} />;

/* ────────── Confirm Dialog ────────── */
const ConfirmDialog = ({ open, title, message, confirmLabel = "Confirm", confirmVariant = "danger", onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div className="cdlg-overlay" role="dialog" aria-modal="true">
      <div className="cdlg-box">
        <div className={`cdlg-icon-wrap cdlg-icon-${confirmVariant}`}>
          <TrashIcon size={26} />
        </div>
        <h3 className="cdlg-title">{title}</h3>
        <p className="cdlg-message">{message}</p>
        <div className="cdlg-actions">
          <button type="button" className="cdlg-btn cdlg-btn-cancel" onClick={onCancel}>Cancel</button>
          <button type="button" className={`cdlg-btn cdlg-btn-confirm cdlg-btn-${confirmVariant}`} onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
};

/* ────────── Multi-Select ────────── */
const MultiSelect = ({ options, value, onChange, placeholder }) => {
  const [open, setOpen] = useState(false);
  const toggleOption = (opt) => {
    onChange(value.includes(opt) ? value.filter((v) => v !== opt) : [...value, opt]);
  };
  return (
    <div className="bc-multiselect" style={{ position: "relative" }}>
      <div className="bc-multiselect__trigger" onClick={() => setOpen(!open)} tabIndex={0}
        onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false); }}>
        <span className="bc-multiselect__label">
          {value.length === 0 ? placeholder : value.map((v) => <span key={v} className="bc-multiselect__tag">{v}</span>)}
        </span>
        <span className="bc-multiselect__caret">
          {value.length > 0 && (
            <button className="bc-multiselect__clear" onClick={(e) => { e.stopPropagation(); onChange([]); }} tabIndex={-1} type="button">
              <XCircleIcon size={14} />
            </button>
          )}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform .2s ease" }}>
            <path d="m6 9 6 6 6-6" />
          </svg>
        </span>
      </div>
      {open && (
        <div className="bc-multiselect__dropdown" role="listbox">
          {options.map((opt) => (
            <div key={opt} className={`bc-multiselect__option ${value.includes(opt) ? "is-selected" : ""}`}
              role="option" onClick={() => toggleOption(opt)} tabIndex={0}>
              <span className="bc-multiselect__check">{value.includes(opt) && <CheckIcon size={12} stroke={2.5} />}</span>
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ────────── Toggle Switch ────────── */
const ToggleSwitch = ({ checked, onChange, disabled }) => (
  <button type="button" role="switch" aria-checked={checked} disabled={disabled} onClick={onChange}
    className={`dept-toggle ${checked ? "is-on" : "is-off"}`}
    title={checked ? "Click to deactivate" : "Click to activate"}>
    <span className="dept-toggle__thumb" />
  </button>
);

/* ────────── Custom Pagination ────────── */
const CustomPagination = ({ currentPage, totalPages, onPageChange, perPage, totalRows, onPerPageChange }) => {
  const pages = useMemo(() => {
    const arr = [];
    const delta = 2;
    const left = currentPage - delta;
    const right = currentPage + delta + 1;
    let l;
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= left && i < right)) arr.push(i);
    }
    const withEllipsis = [];
    arr.forEach((i) => {
      if (l) {
        if (i - l === 2) withEllipsis.push(l + 1);
        else if (i - l > 2) withEllipsis.push("...");
      }
      withEllipsis.push(i);
      l = i;
    });
    return withEllipsis;
  }, [currentPage, totalPages]);

  return (
    <div className="bc-pagination">
      <div className="bc-pagination__info">
        <span>Showing <b>{totalRows === 0 ? 0 : Math.min((currentPage - 1) * perPage + 1, totalRows)}</b>–
          <b>{Math.min(currentPage * perPage, totalRows)}</b> of <b>{totalRows}</b></span>
        <select className="bc-pagination__perpage" value={perPage} onChange={(e) => onPerPageChange(Number(e.target.value))}>
          {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n} / page</option>)}
        </select>
      </div>
      <div className="bc-pagination__pages">
        <button className="bc-pagination__btn" disabled={currentPage === 1} onClick={() => onPageChange(currentPage - 1)}>
          <ChevronLeftIcon size={16} />
        </button>
        {pages.map((p, i) =>
          p === "..." ? (
            <span key={`e-${i}`} className="bc-pagination__ellipsis">…</span>
          ) : (
            <button key={p} className={`bc-pagination__btn ${p === currentPage ? "is-active" : ""}`} onClick={() => onPageChange(p)}>
              {p}
            </button>
          )
        )}
        <button className="bc-pagination__btn" disabled={currentPage === totalPages || totalPages === 0} onClick={() => onPageChange(currentPage + 1)}>
          <ChevronRightIcon size={16} />
        </button>
      </div>
    </div>
  );
};

/* ────────── Toasts ────────── */
const SuccessToast = ({ message, onClose }) => (
  <div className="dept-toast dept-toast--success" role="alert">
    <span className="dept-toast__icon"><CheckCircleIcon size={20} /></span>
    <span className="dept-toast__msg">{message}</span>
    <button className="dept-toast__close" onClick={onClose} type="button"><CloseIcon size={15} /></button>
  </div>
);
const ErrorToast = ({ message, onClose }) => (
  <div className="dept-toast dept-toast--error" role="alert">
    <span className="dept-toast__icon"><XCircleIcon size={20} /></span>
    <span className="dept-toast__msg">{message}</span>
    <button className="dept-toast__close" onClick={onClose} type="button"><CloseIcon size={15} /></button>
  </div>
);

/* ────────── Empty form ────────── */
const emptyCoupon = {
  code: "",
  discount: "",
  type: "percent",       // percent | flat
  min_order: "",
  max_uses: "",
  expiry_date: "",
  status: "Active",
};

/* ────────── Main Component ────────── */
const Coupon = () => {
  const token = localStorage.getItem("authToken");

  const [loading, setLoading] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [statusFilter, setStatusFilter] = useState([]);
  const [typeFilter, setTypeFilter] = useState([]);

  const [form, setForm] = useState(emptyCoupon);
  const [errors, setErrors] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [messagealert, setMessageAlert] = useState("");
  const [messageType, setMessageType] = useState("");

  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleCleared, setToggleCleared] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const [confirmDialog, setConfirmDialog] = useState({
    open: false, title: "", message: "", onConfirm: null,
    confirmLabel: "Confirm", confirmVariant: "danger",
  });

  const showConfirm = useCallback(({ title, message, onConfirm, confirmLabel = "Confirm", confirmVariant = "danger" }) => {
    setConfirmDialog({ open: true, title, message, onConfirm, confirmLabel, confirmVariant });
  }, []);

  const closeConfirm = useCallback(() => {
    setConfirmDialog((prev) => ({ ...prev, open: false, onConfirm: null }));
  }, []);

  const flashMessage = (msg, type = "success") => {
    setMessageAlert(msg);
    setMessageType(type);
    setTimeout(() => { setMessageAlert(""); setMessageType(""); }, 4500);
  };

  /* ── Fetch ── */
  const fetchCoupons = () => {
    setLoading(true);
    fetch(`${API_BASE_URL}api/coupon?page=1&per_page=1000`, {
      headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => { if (data.status) setCoupons(data.data || []); })
      .catch((err) => console.error("Error fetching coupons:", err))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchCoupons(); }, []);

  /* ── Filter ── */
  const filtered = useMemo(() => {
    let result = coupons;
    if (filterText) {
      const ft = filterText.toLowerCase();
      result = result.filter((c) => c.code?.toLowerCase().includes(ft));
    }
    if (statusFilter.length > 0) result = result.filter((c) => statusFilter.includes(c.status));
    if (typeFilter.length > 0) result = result.filter((c) => typeFilter.includes(c.type));
    return result;
  }, [filterText, statusFilter, typeFilter, coupons]);

  useEffect(() => { setCurrentPage(1); }, [filterText, statusFilter, typeFilter]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [filtered.length, perPage, currentPage]);

  /* ── Validate ── */
  const validate = (data) => {
    const e = {};
    if (!data.code?.trim()) e.code = "Coupon code is required";
    else if (!/^[A-Z0-9_-]{3,20}$/.test(data.code.trim())) e.code = "3–20 uppercase letters, numbers, _ or -";
    if (!data.discount) e.discount = "Discount value is required";
    else if (isNaN(data.discount) || Number(data.discount) <= 0) e.discount = "Must be a positive number";
    else if (data.type === "percent" && Number(data.discount) > 100) e.discount = "Percentage cannot exceed 100";
    if (data.min_order && (isNaN(data.min_order) || Number(data.min_order) < 0)) e.min_order = "Must be a positive number";
    if (data.max_uses && (isNaN(data.max_uses) || Number(data.max_uses) < 1)) e.max_uses = "Must be at least 1";
    if (!data.expiry_date) e.expiry_date = "Expiry date is required";
    return e;
  };

  /* ── Save / Update ── */
  const handleSave = (e) => {
    e.preventDefault();
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    const body = new FormData();
    Object.entries(form).forEach(([k, v]) => body.append(k, v));

    const url = isEditMode
      ? `${API_BASE_URL}api/coupon/${editId}`
      : `${API_BASE_URL}api/coupon`;

    fetch(url, {
      method: "POST",
      headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      body,
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.status) {
          setShowModal(false);
          flashMessage(isEditMode ? "Coupon updated successfully!" : "Coupon created successfully!", "success");
          setForm(emptyCoupon);
          setErrors({});
          setIsEditMode(false);
          setEditId(null);
          fetchCoupons();
        } else {
          flashMessage(data.message || "Failed to save coupon.", "danger");
        }
      })
      .catch(() => flashMessage("Something went wrong!", "danger"));
  };

  /* ── Edit ── */
  const handleEdit = (row) => {
    setIsEditMode(true);
    setEditId(row.id);
    setForm({
      code: row.code || "",
      discount: row.discount || "",
      type: row.type || "percent",
      min_order: row.min_order || "",
      max_uses: row.max_uses || "",
      expiry_date: row.expiry_date ? row.expiry_date.substring(0, 10) : "",
      status: row.status || "Active",
    });
    setErrors({});
    setShowModal(true);
  };

  /* ── Delete single ── */
  const handleDelete = (id) => {
    showConfirm({
      title: "Delete Coupon",
      message: "Are you sure you want to delete this coupon? This action cannot be undone.",
      confirmLabel: "Yes, Delete",
      confirmVariant: "danger",
      onConfirm: () => {
        closeConfirm();
        fetch(`${API_BASE_URL}api/coupon/destroy/${id}`, {
          method: "GET",
          headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
        })
          .then((r) => r.json())
          .then((data) => {
            if (data.status) { flashMessage("Coupon deleted successfully!", "success"); fetchCoupons(); }
            else flashMessage("Failed to delete coupon.", "danger");
          })
          .catch(() => flashMessage("Error deleting coupon.", "danger"));
      },
    });
  };

  /* ── Bulk delete ── */
  const handleBulkDelete = () => {
    if (selectedRows.length === 0) return;
    const count = selectedRows.length;
    showConfirm({
      title: "Delete Selected",
      message: `Delete ${count} coupon${count > 1 ? "s" : ""}? This cannot be undone.`,
      confirmLabel: `Delete ${count} Item${count > 1 ? "s" : ""}`,
      confirmVariant: "danger",
      onConfirm: () => {
        closeConfirm();
        Promise.all(
          selectedRows.map((row) =>
            fetch(`${API_BASE_URL}api/coupon/destroy/${row.id}`, {
              method: "GET",
              headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
            }).then((r) => r.json())
          )
        )
          .then(() => {
            flashMessage(`${count} coupon${count > 1 ? "s" : ""} deleted!`, "success");
            setSelectedRows([]);
            setToggleCleared(!toggleCleared);
            fetchCoupons();
          })
          .catch(() => flashMessage("Error deleting coupons!", "danger"));
      },
    });
  };

  /* ── Toggle Status ── */
  const handleStatusChange = (id, currentStatus) => {
    const newStatus = currentStatus === "Active" ? "InActive" : "Active";
    showConfirm({
      title: "Update Status",
      message: `Change status to "${newStatus}"?`,
      confirmLabel: "Yes, Update",
      confirmVariant: "warning",
      onConfirm: () => {
        closeConfirm();
        setTogglingId(id);
        fetch(`${API_BASE_URL}api/coupon/status/${id}`, {
          method: "GET",
          headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
        })
          .then((r) => r.json())
          .then((data) => {
            if (data.status) { flashMessage(`Status updated to ${newStatus}!`, "success"); fetchCoupons(); }
            else flashMessage("Failed to update status.", "danger");
          })
          .catch(() => flashMessage("Error updating status.", "danger"))
          .finally(() => setTogglingId(null));
      },
    });
  };

  /* ── Open Add modal ── */
  const openAddModal = () => {
    setIsEditMode(false);
    setEditId(null);
    setForm(emptyCoupon);
    setErrors({});
    setShowModal(true);
  };

  /* ── Form change ── */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => { const n = { ...prev }; delete n[name]; return n; });
  };

  /* ── Pagination ── */
  const totalRows = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / perPage));
  const pagedData = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return filtered.slice(start, start + perPage);
  }, [filtered, currentPage, perPage]);

  /* ── Expiry badge ── */
  const expiryBadge = (dateStr) => {
    if (!dateStr) return <span className="cpn-badge cpn-badge--na">No expiry</span>;
    const expiry = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return <span className="cpn-badge cpn-badge--expired">Expired</span>;
    if (diffDays <= 7) return <span className="cpn-badge cpn-badge--soon">{diffDays}d left</span>;
    return <span className="cpn-badge cpn-badge--ok">{expiry.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</span>;
  };

  /* ── Columns ── */
  const columns = useMemo(() => [
    {
      name: "Code",
      selector: (row) => row.code,
      cell: (row) => (
        <div className="cpn-code-cell">
          <TagIcon size={13} />
          <span className="cpn-code">{row.code}</span>
        </div>
      ),
      sortable: true,
      grow: 1.5,
    },
    {
      name: "Discount",
      selector: (row) => row.discount,
      cell: (row) => (
        <span className="cpn-discount-pill">
          {row.type === "percent" ? `${row.discount}%` : `₹${row.discount}`}
        </span>
      ),
      sortable: true,
      width: "120px",
    },
    {
      name: "Type",
      selector: (row) => row.type,
      cell: (row) => (
        <span className={`cpn-type-badge cpn-type--${row.type}`}>
          {row.type === "percent" ? "Percent" : "Flat"}
        </span>
      ),
      sortable: true,
      width: "100px",
    },
    {
      name: "Min Order",
      selector: (row) => row.min_order,
      cell: (row) => <span className="bc-cell-muted">{row.min_order ? `₹${row.min_order}` : "—"}</span>,
      sortable: true,
      width: "120px",
    },
    {
      name: "Max Uses",
      selector: (row) => row.max_uses,
      cell: (row) => <span className="bc-cell-muted">{row.max_uses || "Unlimited"}</span>,
      width: "110px",
    },
    {
      name: "Expiry",
      selector: (row) => row.expiry_date,
      cell: (row) => expiryBadge(row.expiry_date),
      sortable: true,
      width: "150px",
    },
    {
      name: "Status",
      selector: (row) => row.status,
      cell: (row) => (
        <ToggleSwitch
          checked={row.status === "Active"}
          disabled={togglingId === row.id}
          onChange={() => handleStatusChange(row.id, row.status)}
        />
      ),
      sortable: true,
      width: "100px",
      ignoreRowClick: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="bc-row-actions">
          <button className="bc-ico-btn edit" onClick={() => handleEdit(row)} title="Edit"><EditIcon size={15} /></button>
          <button className="bc-ico-btn del" onClick={() => handleDelete(row.id)} title="Delete"><TrashIcon size={15} /></button>
        </div>
      ),
      width: "110px",
      right: true,
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ], [togglingId]);

  /* ── Table Styles ── */
  const tableStyles = {
    table: { style: { background: "transparent" } },
    headRow: {
      style: {
        background: "#f0fbf6",
        borderTopLeftRadius: "12px",
        borderTopRightRadius: "12px",
        borderBottom: "1px solid #d6efe2",
        minHeight: "52px",
      },
    },
    headCells: {
      style: {
        fontSize: "11.5px",
        fontWeight: 700,
        letterSpacing: "0.07em",
        textTransform: "uppercase",
        color: "#5b6b66",
        paddingLeft: "16px",
        paddingRight: "16px",
      },
    },
    rows: {
      style: {
        minHeight: "64px",
        fontSize: "14px",
        color: "#1f2a37",
        borderBottom: "1px solid #eef4f1 !important",
        transition: "background 0.18s ease",
      },
      highlightOnHoverStyle: { background: "#f6fcf9", borderBottomColor: "#e3f0e9", outline: "none" },
      stripedStyle: { background: "#fafefb" },
    },
    cells: { style: { paddingLeft: "16px", paddingRight: "16px" } },
    pagination: { style: { display: "none" } },
  };

  /* ── Render ── */
  return (
    <>
      <style>{`
        .cpn-code-cell { display:flex; align-items:center; gap:6px; }
        .cpn-code { font-family:monospace; font-weight:700; font-size:13px; color:#1a7a4a; letter-spacing:.05em; }
        .cpn-discount-pill { background:#e6f7ee; color:#1a7a4a; border:1px solid #b3e6ca; border-radius:20px; padding:3px 10px; font-size:13px; font-weight:700; }
        .cpn-type-badge { border-radius:6px; padding:3px 10px; font-size:12px; font-weight:600; }
        .cpn-type--percent { background:#eff6ff; color:#1d4ed8; border:1px solid #bfdbfe; }
        .cpn-type--flat { background:#fef3c7; color:#92400e; border:1px solid #fde68a; }
        .cpn-badge { border-radius:6px; padding:3px 9px; font-size:12px; font-weight:600; }
        .cpn-badge--ok { background:#e6f7ee; color:#166534; border:1px solid #bbf7d0; }
        .cpn-badge--soon { background:#fff7ed; color:#c2410c; border:1px solid #fed7aa; }
        .cpn-badge--expired { background:#fef2f2; color:#b91c1c; border:1px solid #fecaca; }
        .cpn-badge--na { background:#f3f4f6; color:#6b7280; border:1px solid #e5e7eb; }
        .cpn-form-row { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
        .cpn-type-toggle { display:flex; gap:8px; }
        .cpn-type-btn { flex:1; padding:9px 0; border-radius:8px; border:1.5px solid #d1d5db; background:#fff; font-size:13px; font-weight:600; cursor:pointer; color:#374151; transition:all .18s; }
        .cpn-type-btn.is-active { border-color:#10b981; background:#e6f7ee; color:#065f46; }
        @media(max-width:600px){ .cpn-form-row { grid-template-columns:1fr; } }
      `}</style>

      <div id="layout-wrapper">
        <Header />

        {loading && (
          <div className="bc-overlay">
            <div className="bc-spinner" />
          </div>
        )}

        <div className="main-content">
          <div className="page-content">
            <div className="bc-shell">

              {/* ── Page Header ── */}
              <div className="bc-head">
                <div>
                  <h2>Coupons</h2>
                  <p>Manage discount codes for cart and checkout.</p>
                </div>
                <button type="button" className="bc-add-btn" onClick={openAddModal}>
                  <PlusIcon size={18} />
                  Add Coupon
                </button>
              </div>

              {/* ── Toast ── */}
              {messagealert && messageType === "success" && (
                <SuccessToast message={messagealert} onClose={() => setMessageAlert("")} />
              )}
              {messagealert && messageType === "danger" && (
                <ErrorToast message={messagealert} onClose={() => setMessageAlert("")} />
              )}

              {/* ── Main Card ── */}
              <div className="bc-card">
                {/* Toolbar */}
                <div className="bc-card-top">
                  <div className="bc-card-top-left">
                    <div className="bc-search-box">
                      <SearchIcon size={17} />
                      <input
                        type="text"
                        placeholder="Search by coupon code…"
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value.toUpperCase())}
                      />
                    </div>
                    <MultiSelect
                      options={["Active", "InActive"]}
                      value={statusFilter}
                      onChange={setStatusFilter}
                      placeholder="Filter by status…"
                    />
                    <MultiSelect
                      options={["percent", "flat"]}
                      value={typeFilter}
                      onChange={setTypeFilter}
                      placeholder="Filter by type…"
                    />
                  </div>
                  <div className="bc-total-badge">
                    <TagIcon size={16} />
                    Total:&nbsp;<b>{filtered.length}</b>
                  </div>
                </div>

                {/* Bulk action bar */}
                {selectedRows.length > 0 && (
                  <div className="bc-bulk-bar">
                    <div className="bc-bulk-count">
                      <span className="bc-num-badge">{selectedRows.length}</span>
                      coupon{selectedRows.length > 1 ? "s" : ""} selected
                    </div>
                    <div className="bc-bulk-actions">
                      <button className="bc-bulk-btn ghost" type="button"
                        onClick={() => { setSelectedRows([]); setToggleCleared(!toggleCleared); }}>
                        Clear
                      </button>
                      <button className="bc-bulk-btn danger" type="button" onClick={handleBulkDelete}>
                        <TrashIcon size={14} /> Delete Selected
                      </button>
                    </div>
                  </div>
                )}

                {/* Table */}
                <DataTable
                  columns={columns}
                  data={pagedData}
                  customStyles={tableStyles}
                  highlightOnHover
                  responsive
                  striped
                  selectableRows
                  onSelectedRowsChange={({ selectedRows }) => setSelectedRows(selectedRows)}
                  clearSelectedRows={toggleCleared}
                  noDataComponent={
                    <div className="bc-empty">
                      <TagIcon size={40} />
                      <p>No coupons found</p>
                      <span>Try adjusting your search or add a new coupon</span>
                    </div>
                  }
                />

                {filtered.length > 0 && (
                  <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    perPage={perPage}
                    totalRows={totalRows}
                    onPerPageChange={(n) => { setPerPage(n); setCurrentPage(1); }}
                  />
                )}
              </div>
            </div>
          </div>

          {/* ── Add / Edit Modal ── */}
          {showModal && (
            <div className="bc-modal-overlay"
              onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}
              aria-modal="true" role="dialog">
              <div className="bc-modal">

                {/* Header */}
                <div className="bc-modal__header">
                  <div className="bc-modal__header-left">
                    <div className="bc-modal__icon"><TagIcon size={20} /></div>
                    <div>
                      <p className="bc-modal__title">{isEditMode ? "Edit Coupon" : "Add New Coupon"}</p>
                      <p className="bc-modal__subtitle">
                        {isEditMode ? "Update the coupon details" : "Create a discount code for cart and checkout"}
                      </p>
                    </div>
                  </div>
                  <button type="button" className="bc-modal__close" onClick={() => setShowModal(false)}>
                    <CloseIcon size={16} />
                  </button>
                </div>

                <form onSubmit={handleSave}>
                  <div className="bc-modal__body">

                    {/* Coupon Code */}
                    <div className="bc-form-group">
                      <label className="bc-form-label" htmlFor="cpnCode">
                        Coupon Code <span>*</span>
                      </label>
                      <input
                        id="cpnCode"
                        type="text"
                        name="code"
                        className={`bc-form-control ${errors.code ? "is-invalid" : ""}`}
                        placeholder="e.g. SAVE20"
                        value={form.code}
                        onChange={(e) => handleChange({ target: { name: "code", value: e.target.value.toUpperCase() } })}
                        autoFocus
                      />
                      {errors.code && <small className="bc-field-error">{errors.code}</small>}
                    </div>

                    {/* Discount Type */}
                    <div className="bc-form-group">
                      <label className="bc-form-label">Discount Type <span>*</span></label>
                      <div className="cpn-type-toggle">
                        <button type="button"
                          className={`cpn-type-btn ${form.type === "percent" ? "is-active" : ""}`}
                          onClick={() => setForm((p) => ({ ...p, type: "percent" }))}>
                          <PercentIcon size={13} /> &nbsp;Percentage
                        </button>
                        <button type="button"
                          className={`cpn-type-btn ${form.type === "flat" ? "is-active" : ""}`}
                          onClick={() => setForm((p) => ({ ...p, type: "flat" }))}>
                          ₹ &nbsp;Flat Amount
                        </button>
                      </div>
                    </div>

                    {/* Discount + Min Order */}
                    <div className="cpn-form-row">
                      <div className="bc-form-group" style={{ margin: 0 }}>
                        <label className="bc-form-label">
                          {form.type === "percent" ? "Discount (%)" : "Discount (₹)"} <span>*</span>
                        </label>
                        <input
                          type="number"
                          name="discount"
                          className={`bc-form-control ${errors.discount ? "is-invalid" : ""}`}
                          placeholder={form.type === "percent" ? "e.g. 20" : "e.g. 100"}
                          value={form.discount}
                          onChange={handleChange}
                          min="0"
                          max={form.type === "percent" ? "100" : undefined}
                          step="0.01"
                        />
                        {errors.discount && <small className="bc-field-error">{errors.discount}</small>}
                      </div>

                      <div className="bc-form-group" style={{ margin: 0 }}>
                        <label className="bc-form-label">Min Order Amount (₹)</label>
                        <input
                          type="number"
                          name="min_order"
                          className={`bc-form-control ${errors.min_order ? "is-invalid" : ""}`}
                          placeholder="e.g. 500 (optional)"
                          value={form.min_order}
                          onChange={handleChange}
                          min="0"
                          step="0.01"
                        />
                        {errors.min_order && <small className="bc-field-error">{errors.min_order}</small>}
                      </div>
                    </div>

                    {/* Max Uses + Expiry */}
                    <div className="cpn-form-row">
                      <div className="bc-form-group" style={{ margin: 0 }}>
                        <label className="bc-form-label">Max Uses</label>
                        <input
                          type="number"
                          name="max_uses"
                          className={`bc-form-control ${errors.max_uses ? "is-invalid" : ""}`}
                          placeholder="Leave blank for unlimited"
                          value={form.max_uses}
                          onChange={handleChange}
                          min="1"
                        />
                        {errors.max_uses && <small className="bc-field-error">{errors.max_uses}</small>}
                      </div>

                      <div className="bc-form-group" style={{ margin: 0 }}>
                        <label className="bc-form-label">Expiry Date <span>*</span></label>
                        <input
                          type="date"
                          name="expiry_date"
                          className={`bc-form-control ${errors.expiry_date ? "is-invalid" : ""}`}
                          value={form.expiry_date}
                          onChange={handleChange}
                          min={new Date().toISOString().split("T")[0]}
                        />
                        {errors.expiry_date && <small className="bc-field-error">{errors.expiry_date}</small>}
                      </div>
                    </div>

                    {/* Status Toggle */}
                    <div className="bc-form-group">
                      <label className="bc-form-label">Status</label>
                      <div className="dept-status-row">
                        <div>
                          <div className="dept-status-row-label">Active Status</div>
                          <div className="dept-status-row-hint">Toggle to enable or disable this coupon</div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <span className={`dept-status-indicator ${form.status === "Active" ? "active" : "inactive"}`}>
                            {form.status === "Active" ? "Active" : "Inactive"}
                          </span>
                          <ToggleSwitch
                            checked={form.status === "Active"}
                            onChange={() => setForm((p) => ({ ...p, status: p.status === "Active" ? "InActive" : "Active" }))}
                          />
                        </div>
                      </div>
                    </div>

                  </div>

                  {/* Footer */}
                  <div className="bc-modal__footer">
                    <button type="button" className="bc-btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
                    <button type="submit" className="bc-btn-submit">
                      {isEditMode ? <><EditIcon size={15} /> Update Coupon</> : <><PlusIcon size={15} /> Add Coupon</>}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* ── Confirm Dialog ── */}
          <ConfirmDialog
            open={confirmDialog.open}
            title={confirmDialog.title}
            message={confirmDialog.message}
            confirmLabel={confirmDialog.confirmLabel}
            confirmVariant={confirmDialog.confirmVariant}
            onConfirm={confirmDialog.onConfirm}
            onCancel={closeConfirm}
          />

          <Footer />
        </div>
      </div>
    </>
  );
};

export default Coupon;